import {
  Container,
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Divider,
  Avatar,
  Badge,
  SimpleGrid,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Button,
  useColorModeValue,
  IconButton,
  Tooltip,
  useToast,
  Spinner,
  useBreakpointValue,
  Textarea,
} from '@chakra-ui/react';
import {
  User,
  BookOpen,
  Images,
  GearSix,
  SignOut,
  PencilLine,
  FloppyDisk,
  X,
  Check,
} from '@phosphor-icons/react';
import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { auth, db } from '../firebase/config';
import {
  getUserProfile,
  updateUserProfile,
} from '../firebase/siteProfile';
import { getDocs, collection, query, where } from 'firebase/firestore';

const StatCard = ({ icon, label, value }) => (
  <VStack bg="bg.surface" p={4} borderRadius="md" borderWidth="1px" borderColor="border.default" spacing={1} align="flex-start">
    <HStack>
      {icon}
      <Text fontFamily="heading" fontSize="lg">{label}</Text>
    </HStack>
    <Text fontSize="2xl" fontWeight="bold" pl={8}>{value}</Text>
  </VStack>
);

export default function Account() {
  const toast = useToast();
  const [profileData, setProfileData] = useState(null);
  const [userStats, setUserStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({ displayName: '', bio: '' });
  const [savingProfile, setSavingProfile] = useState(false);

  const userId = auth.currentUser?.uid;

  const isMobile = useBreakpointValue({ base: true, md: false });

  useEffect(() => {
    const fetchUserData = async () => {
      if (!userId) return;
      try {
        setLoading(true);
        const profileResponse = await getUserProfile(userId);
        if (profileResponse.success) {
          setProfileData(profileResponse.data);
          setFormData({
            displayName: profileResponse.data.displayName || '',
            bio: profileResponse.data.bio || '',
          });
        }

        const sitesSnap = await getDocs(query(collection(db, 'sites'), where('owner', '==', userId)));
        const commentsSnap = await getDocs(query(collection(db, 'site_discussions'), where('userId', '==', userId)));

        setUserStats({
          sites: sitesSnap.size,
          comments: commentsSnap.size,
          reputation: profileResponse.success ? (profileResponse.data.reputation || 0) : 0,
        });
      } catch (error) {
        toast({
          title: 'Error loading profile',
          description: 'Failed to load user profile data.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };
    fetchUserData();
  }, [userId, toast]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSavingProfile(true);
      const result = await updateUserProfile(userId, formData);
      if (result.success) {
        toast({ title: 'Profile updated', status: 'success', duration: 3000 });
        setProfileData((prev) => ({ ...prev, ...formData }));
        setEditMode(false);
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      toast({ title: 'Error updating profile', description: error.message, status: 'error', duration: 5000 });
    } finally {
      setSavingProfile(false);
    }
  };

  if (loading) {
    return (
      <Flex justify="center" align="center" minH="80vh">
        <Spinner size="xl" />
      </Flex>
    );
  }

  const renderProfileContent = () => (
    <VStack as="form" onSubmit={handleSubmit} spacing={6} align="stretch">
      <HStack justify="space-between">
        <Heading fontFamily="heading" size="lg">Adventurer's Profile</Heading>
        <Tooltip label={editMode ? 'Cancel' : 'Edit Profile'}>
          <IconButton
            icon={editMode ? <X /> : <PencilLine />}
            onClick={() => setEditMode(!editMode)}
            variant="ghost"
          />
        </Tooltip>
      </HStack>
      <FormControl>
        <FormLabel fontFamily="heading">Display Name</FormLabel>
        <Input
          name="displayName"
          value={formData.displayName}
          onChange={handleInputChange}
          isReadOnly={!editMode}
        />
      </FormControl>
      <FormControl>
        <FormLabel fontFamily="heading">Bio</FormLabel>
        <Textarea
          name="bio"
          value={formData.bio}
          onChange={handleInputChange}
          isReadOnly={!editMode}
          rows={4}
          variant="adventure"
        />
      </FormControl>
      {editMode && (
        <HStack justify="flex-end">
          <Button
            variant="adventure"
            onClick={() => setEditMode(false)}
            leftIcon={<X />}
          >
            Cancel
          </Button>
          <Button
            variant="adventure"
            type="submit"
            isLoading={savingProfile}
            leftIcon={<FloppyDisk />}
          >
            Save
          </Button>
        </HStack>
      )}
      <Divider borderColor="border.default" />
      <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={6}>
        <StatCard icon={<BookOpen />} label="Sites Submitted" value={userStats?.sites || 0} />
        <StatCard icon={<Images />} label="Comments" value={userStats?.comments || 0} />
        <StatCard icon={<User />} label="Reputation" value={userStats?.reputation || 0} />
      </SimpleGrid>
    </VStack>
  );

  const renderJournalContent = () => <Text>Your adventure journal entries will appear here.</Text>;
  const renderGalleryContent = () => <Text>A gallery of your discovered artifacts and photos.</Text>;

  return (
    <Box bg="bg.default" color="text.default" minH="100vh" py={10}
         _before={{
           content: '""',
           position: 'absolute',
           top: 0, left: 0, right: 0, bottom: 0,
           backgroundImage: "url('/textures/parchment.png')",
           backgroundRepeat: 'repeat',
           opacity: 0.5,
           zIndex: -1,
         }}>
      <Container maxW="container.xl">
        <Flex direction={{ base: 'column', md: 'row' }} gap={10}>
          {/* Left Column / Mobile Header */}
          <VStack w={{ base: 'full', md: '300px' }} spacing={6} align="stretch">
            <VStack spacing={4}>
              <Avatar
                size="2xl"
                name={profileData?.displayName}
                src={profileData?.photoURL}
                border="4px solid"
                borderColor="border.default"
              />
              <Heading size="xl" fontFamily="heading">{profileData?.displayName}</Heading>
              <Text fontFamily="accent" fontSize="lg">Level 12 Adventurer</Text>
            </VStack>
            <Divider borderColor="border.default" />
            {isMobile ? (
              <Tabs isFitted variant="adventure">
                <TabList>
                  <Tab>Profile</Tab>
                  <Tab>Journal</Tab>
                  <Tab>Gallery</Tab>
                </TabList>
                <TabPanels>
                  <TabPanel>{renderProfileContent()}</TabPanel>
                  <TabPanel>{renderJournalContent()}</TabPanel>
                  <TabPanel>{renderGalleryContent()}</TabPanel>
                </TabPanels>
              </Tabs>
            ) : (
              <VStack as="nav" align="stretch" spacing={4}>
                <Button leftIcon={<User />} justifyContent="flex-start" variant="ghost">Profile</Button>
                <Button leftIcon={<BookOpen />} justifyContent="flex-start" variant="ghost">Journal</Button>
                <Button leftIcon={<Images />} justifyContent="flex-start" variant="ghost">Gallery</Button>
                <Button leftIcon={<GearSix />} justifyContent="flex-start" variant="ghost">Settings</Button>
                <Divider borderColor="border.default" />
                <Button leftIcon={<SignOut />} justifyContent="flex-start" variant="ghost">Logout</Button>
              </VStack>
            )}
          </VStack>

          {/* Right Column (Desktop Only) */}
          {!isMobile && (
            <Box flex="1">
              <Tabs variant="adventure">
                <TabList>
                  <Tab>Profile</Tab>
                  <Tab>Journal</Tab>
                  <Tab>Gallery</Tab>
                </TabList>
                <TabPanels>
                  <TabPanel>{renderProfileContent()}</TabPanel>
                  <TabPanel>{renderJournalContent()}</TabPanel>
                  <TabPanel>{renderGalleryContent()}</TabPanel>
                </TabPanels>
              </Tabs>
            </Box>
          )}
        </Flex>
      </Container>
    </Box>
  );
}
